// Screens/ContactQueriesManagement/ContactQueriesManagement.jsx
import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "../../Components/Layout/DashboardLayout";
import CustomTable from "../../Components/CustomTable";
import CustomModal from "../../Components/CustomModal";
import CustomPagination from "../../Components/CustomPagination";
import CustomButton from "../../Components/CustomButton";
import { toast } from "react-toastify";
import { useErrorBoundary } from "../../Hooks/useErrorBoundary";
import "./ContactQueries.css";

export const ContactQueriesManagement = () => {
  const { handleError } = useErrorBoundary();
  const [queries, setQueries] = useState([]);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [showModal, setShowModal] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const toastId = useRef(null);

  const token = localStorage.getItem("login") || "";
  const apiUrl = process.env.REACT_APP_BASE_URL;

  /* ===============================
     SAFE TOAST
  =============================== */

  const showToast = (type, message) => {
    if (toastId.current) toast.dismiss(toastId.current);
    toastId.current = toast[type](message, {
      autoClose: 2500,
    });
  };

  /* ===============================
     SAFE FETCH JSON
  =============================== */

  const safeParse = async (res) => {
    const contentType = res.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await res.json();
    }
    const text = await res.text();
    throw new Error("Server error. Please try again later.");
  };

  /* ===============================
     FETCH CONTACT QUERIES
  =============================== */

  const fetchQueries = async (page = 1) => {
    document.querySelector(".loaderBox")?.classList.remove("d-none");

    try {
      const res = await fetch(`${apiUrl}/admin/contact-queries?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await safeParse(res);

      if (data?.status) {
        // Handle both paginated and non-paginated responses
        if (data.data?.data) {
          setQueries(data.data.data);
          setPagination(data.data);
        } else {
          setQueries(data.data || []);
          setPagination({
            total: data.data?.length || 0,
            per_page: 10,
            current_page: page,
            last_page: Math.ceil((data.data?.length || 0) / 10),
          });
        }
        setCurrentPage(page);
      } else {
        showToast("error", data?.message || "Failed to fetch queries");
      }

    } catch (error) {
      console.error(error);
      handleError(error);
    } finally {
      document.querySelector(".loaderBox")?.classList.add("d-none");
    }
  };

  useEffect(() => {
    fetchQueries(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===============================
     FORMAT DATE
  =============================== */

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* ===============================
     GET REASON BADGE CLASS
  =============================== */

  const getReasonBadgeClass = (reason) => {
    const reasons = {
      "Support": "cq-badge-support",
      "Sales": "cq-badge-sales",
      "General": "cq-badge-general",
      "Technical": "cq-badge-tech",
    };
    return reasons[reason] || "cq-badge-default";
  };

  /* ===============================
     VIEW QUERY DETAILS
  =============================== */

  const handleViewQuery = (query) => {
    setSelectedQuery(query);
    setShowModal(true);
  };

  /* ===============================
     RENDER
  =============================== */

  return (
    <DashboardLayout>
      <div className="dashCard cq-page-wrapper">

        <div className="cq-header-section">
          <h2 className="cq-main-title">Contact Queries</h2>
          <CustomButton
            text="↻ Refresh"
            onClick={() => fetchQueries(currentPage)}
            disabled={submitting}
          />
        </div>

        {/* ✅ Table Wrapper with Horizontal Scroll */}
        <div className="cq-table-responsive">
          <CustomTable
            headers={[
            //   { key: "id", title: "ID" },
              { key: "name", title: "Name" },
              { key: "email", title: "Email" },
              { key: "phone", title: "Phone" },
              { key: "reason", title: "Reason" },
              { key: "message", title: "Message" },
              { key: "created_at", title: "Submitted" },
              { key: "action", title: "Action" },
            ]}
          >
            <tbody>
              {queries?.length > 0 ? (
                queries?.map((item, index) => (
                  <tr key={item.id} className="cq-table-row">
                    {/* <td className="cq-cell-id">#{item.id}</td> */}
                    <td className="cq-cell-name"><strong>{item?.name || "N/A"}</strong></td>
                    <td className="cq-cell-email">
                      <a href={`mailto:${item.email}`} className="cq-email-link">
                        {item?.email || "N/A"}
                      </a>
                    </td>
                    <td className="cq-cell-phone">{item?.phone || "N/A"}</td>
                    <td className="cq-cell-reason">
                      <span className={`cq-status-badge ${getReasonBadgeClass(item?.reason)}`}>
                        {item?.reason || "General"}
                      </span>
                    </td>
                    <td className="cq-cell-message cq-message-column">
                      {item?.message?.length > 60 
                        ? `${item.message.substring(0, 60)}...` 
                        : item?.message || "N/A"}
                    </td>
                    <td className="cq-cell-date">
                      <small className="cq-text-muted">
                        {formatDate(item?.created_at)}
                      </small>
                    </td>
                    <td className="cq-cell-action">
                      <div className="cq-action-wrapper">
                        <button
                          className="cq-action-btn"
                          onClick={() => handleViewQuery(item)}
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="cq-empty-state">
                    No contact queries found
                  </td>
                </tr>
              )}
            </tbody>
          </CustomTable>
        </div>

        {pagination?.total > 0 && (
          <CustomPagination
            itemsPerPage={pagination?.per_page || 10}
            totalItems={pagination?.total || 0}
            currentPage={currentPage}
            onPageChange={(page) => fetchQueries(page)}
          />
        )}

        {/* View Query Modal */}
        <CustomModal
          show={showModal}
          close={() => {
            setShowModal(false);
            setSelectedQuery(null);
          }}
          heading="Query Details"
        >
          {selectedQuery && (
            <div className="cq-modal-form cq-animate-modal">
              
              <div className="cq-detail-row">
                <label className="cq-detail-label">Name</label>
                <p className="cq-detail-value">{selectedQuery.name || "N/A"}</p>
              </div>

              <div className="cq-detail-row">
                <label className="cq-detail-label">Email</label>
                <p className="cq-detail-value">
                  <a href={`mailto:${selectedQuery.email}`} className="cq-email-link">
                    {selectedQuery.email || "N/A"}
                  </a>
                </p>
              </div>

              <div className="cq-detail-row">
                <label className="cq-detail-label">Phone</label>
                <p className="cq-detail-value">{selectedQuery.phone || "N/A"}</p>
              </div>

              <div className="cq-detail-row">
                <label className="cq-detail-label">Reason</label>
                <p className="cq-detail-value">
                  <span className={`cq-status-badge ${getReasonBadgeClass(selectedQuery.reason)}`}>
                    {selectedQuery.reason || "General"}
                  </span>
                </p>
              </div>

              <div className="cq-detail-row">
                <label className="cq-detail-label">Message</label>
                <p className="cq-detail-value cq-message-full">{selectedQuery.message || "N/A"}</p>
              </div>

              <div className="cq-detail-row">
                <label className="cq-detail-label">Submitted At</label>
                <p className="cq-detail-value cq-text-muted">
                  {formatDate(selectedQuery.created_at)}
                </p>
              </div>

              <div className="cq-modal-actions">
                <CustomButton
                  text="Close"
                  variant="cq-secondary-btn"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedQuery(null);
                  }}
                />
                {selectedQuery.email && (
                  <a 
                    href={`mailto:${selectedQuery.email}?subject=Re: ${selectedQuery.reason} Inquiry`}
                    className="cq-btn-reply"
                  >
                    Reply via Email
                  </a>
                )}
              </div>

            </div>
          )}
        </CustomModal>

      </div>
    </DashboardLayout>
  );
};

export default ContactQueriesManagement;